import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants';
import { useUsers, type UserListItem } from '@/api/useUsers';

const KEYS = {
  all: ['audit-log'] as const,
  list: (filters?: Record<string, string>, page?: number) =>
    [...KEYS.all, 'list', filters, page] as const,
};

export interface AuditLogFilters {
  model_name?: string;
  action?: string;
  object_id?: string;
  user_id?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
  [key: string]: string | undefined;
}

export interface AuditLogEntry {
  id: string;
  user: number | null;
  user_email: string | null;
  action: 'create' | 'update' | 'delete' | 'revert';
  model_name: string;
  object_id: string;
  object_repr: string;
  changes: Record<string, { old: unknown; new: unknown }>;
  snapshot: Record<string, unknown>;
  ip_address: string | null;
  user_agent: string;
  timestamp: string;
  reverted_from: string | null;
}

export interface AuditLogResponse {
  results: AuditLogEntry[];
  count: number;
  next: string | null;
  previous: string | null;
}

const PAGE_SIZE = 15;

export function useAuditLog(filters?: Record<string, string>, page = 1) {
  return useQuery({
    queryKey: KEYS.list(filters, page),
    queryFn: async () => {
      const params = new URLSearchParams(filters);
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String((page - 1) * PAGE_SIZE));
      const { data } = await api.get(`${API_ROUTES.auditLog}?${params}`);
      return data as AuditLogResponse;
    },
  });
}

export function useAuditUsers(): { data: UserListItem[] | undefined; isLoading: boolean } {
  return useUsers();
}

export function useRevertAudit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (auditId: string) => {
      const { data } = await api.post(`${API_ROUTES.auditLog}${auditId}/revert/`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}
